import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { IPublicClientApplication } from '@azure/msal-browser';
import { apiScope } from '../auth/authConfig';

class ApiClient {
  private client: AxiosInstance;
  private msalInstance: IPublicClientApplication | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || '/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use(
      async (config) => {
        if (this.msalInstance) {
          const accounts = this.msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            try {
              const response = await this.msalInstance.acquireTokenSilent({
                scopes: [apiScope],
                account: accounts[0],
              });
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            } catch (error) {
              console.error('Token acquisition failed:', error);
              // Fallback to interactive login
              try {
                const response = await this.msalInstance.acquireTokenPopup({
                  scopes: [apiScope],
                  account: accounts[0],
                });
                config.headers.Authorization = `Bearer ${response.accessToken}`;
              } catch (popupError) {
                console.error('Interactive token acquisition failed:', popupError);
              }
            }
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized
          console.error('Unauthorized access');
        }
        return Promise.reject(error);
      }
    );
  }

  setMsalInstance(instance: IPublicClientApplication) {
    this.msalInstance = instance;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const apiClient = new ApiClient();
